import {ofType, StateObservable} from "redux-observable";
import { Observable, of } from 'rxjs';
import { ajax } from 'rxjs/ajax';
import { mergeMap, map, catchError, debounceTime, exhaustMap } from 'rxjs/operators';
import {EVENT_NOTIFICATION_TYPE} from "../../helpers/EventNotificationType";
import {eventNotificationError, eventNotificationShow, eventNotificationSuccess} from "./EventNotificationActions";
import {APP_CONFIG} from "../../config/GeneralConfig";
import {
    UserViewSettings, UserViewSettingsId,
    UserViewSettingsProp,
    ViewIdentifiable,
    ViewIdentifier
} from "../../models/common/ViewSettings";
import {ErrorAction, ReduxAction} from "../../models/common/ActionTypes";
import {
    ActiveDataFilter,
    SingleActiveDataFilterProp
} from "../../models/common/DataFilter";
import {ColumnList, PageChangeParams, SortModel} from "../../models/common/DataTable";
import {UserId} from "../../models/users/AuthenticatedUser";
import {AppState} from "../../reducers/RootReducer";
import {getUrlParamsFromViewSettings, RouteMethods} from "../../config/RouteConfig";
import {getUserViewSettingsApiRequest} from "../../models/common/ApiReturnType";


/**
 * typescript declarations
 */
 export type ViewActionTypes =  ViewSearchActionTypes
    | FetchUserViewSettingsActionTypes
    | ViewSortChangeAction
    | ViewRowsPerPageChangeAction
    | ViewPageNumberChangeAction
    | ViewDataFilterActionTypes
    | ChangeViewTitleAction
    | SendUserToPageActionTypes
    | InitialUserViewSettings
    | UserViewSettingsChangeAction


/**
 * init settings
 */
export interface InitialUserViewSettings extends ViewIdentifiable, ReduxAction<typeof INITIAL_USER_VIEW_SETTINGS> {
    userId?: UserId
    /**  */
    defaultViewSettings?: Partial< UserViewSettings >
}

export const INITIAL_USER_VIEW_SETTINGS = "INITIAL_USER_VIEW_SETTINGS";

export function initUserViewSettings(
    viewIdentifier: string,
    userId?: UserId,
    defaultViewSettings?: Partial< UserViewSettings >
): InitialUserViewSettings {
    return {
        type: INITIAL_USER_VIEW_SETTINGS,
        viewIdentifier,
        userId,
        defaultViewSettings
    };
}

/**
 * view settings change
 */
export interface UserViewSettingsChangeAction extends ViewIdentifiable, UserViewSettingsProp, ReduxAction<typeof USER_VIEW_SETTINGS_CHANGE> {}

export const USER_VIEW_SETTINGS_CHANGE = "USER_VIEW_SETTINGS_CHANGE";

export function changeUserViewSettings( settings: UserViewSettings ): UserViewSettingsChangeAction {
   return {
       type: USER_VIEW_SETTINGS_CHANGE,
       userViewSettings: settings,
       viewIdentifier: settings.viewIdentifier.rawValue
   };
}

/**
 * FETCH settings
 */
interface FetchUserViewSettingsAction extends ViewIdentifiable {
    type: typeof FETCH_USER_VIEW_SETTINGS
 }
 interface FetchUserViewSettingsSuccessAction extends ViewIdentifiable {
     type: typeof FETCH_USER_VIEW_SETTINGS_SUCCESS
     settings: UserViewSettings
 }

 interface FetchUserViewSettingsErrorAction extends ViewIdentifiable {
     type: typeof FETCH_USER_VIEW_SETTINGS_ERROR
     error: any
 }
 export type FetchUserViewSettingsActionTypes = FetchUserViewSettingsAction | FetchUserViewSettingsSuccessAction | FetchUserViewSettingsErrorAction

/**
 * fetch user view settings for current view
 */
export const FETCH_USER_VIEW_SETTINGS = "FETCH_USER_VIEW_SETTINGS";
export const FETCH_USER_VIEW_SETTINGS_SUCCESS = "FETCH_USER_VIEW_SETTINGS_SUCCESS";
export const FETCH_USER_VIEW_SETTINGS_ERROR = "FETCH_USER_VIEW_SETTINGS_ERROR";

export function fetchUserViewSettings( viewIdentifier: string ): FetchUserViewSettingsAction {
    return {
        type: FETCH_USER_VIEW_SETTINGS,
        viewIdentifier
    };
}
export function fetchUserViewSettingsSuccess( viewIdentifier: string, settings: UserViewSettings ): FetchUserViewSettingsSuccessAction {
    return {
        type: FETCH_USER_VIEW_SETTINGS_SUCCESS,
        viewIdentifier,
        settings
    };
}
export function fetchUserViewSettingsError( viewIdentifier: string, error: any ): FetchUserViewSettingsErrorAction {
    return {
        type: FETCH_USER_VIEW_SETTINGS_ERROR,
        viewIdentifier,
        error
    };
}

export function fetchUserViewSettingsEpic(
    action$: Observable< FetchUserViewSettingsAction>,
    store$: StateObservable< AppState >
) {
    return action$.pipe(
        ofType(FETCH_USER_VIEW_SETTINGS),
        mergeMap(action => {
            const request = getUserViewSettingsApiRequest(
                `${APP_CONFIG.baseWeb}users/views/${action.viewIdentifier}/settings/`,
                action.viewIdentifier,
                store$
            );

            return ajax(APP_CONFIG.getRequestObject(request.path)).pipe(
                map(response => fetchUserViewSettingsSuccess(action.viewIdentifier, response.response as UserViewSettings )),
                catchError(error =>
                    of(
                        fetchUserViewSettingsError(action.viewIdentifier, error),
                        eventNotificationShow(
                            EVENT_NOTIFICATION_TYPE.customAjaxError,
                            "Nutzereinstellungen für diese Seite konnten nicht geladen werden!"
                        ))
                ))
        })
    );
}


/**
 * fetch top 10 settings
 *
 * fetch the 10 most used user view settings by view and user
 */
export interface FetchTopUserViewSettingsAction extends ViewIdentifiable, ReduxAction<typeof FETCH_TOP_USER_VIEW_SETTINGS>, UserViewSettingsProp {}
export interface FetchTopUserViewSettingsSuccessAction extends ViewIdentifiable, ReduxAction<typeof FETCH_TOP_USER_VIEW_SETTINGS_SUCCESS> {
    topSettings: UserViewSettings[]
}
export interface FetchTopUserViewSettingsErrorAction extends ErrorAction<typeof FETCH_TOP_USER_VIEW_SETTINGS_ERROR> {}

export type FetchTopUserViewSettingsActionTypes = FetchTopUserViewSettingsAction | FetchTopUserViewSettingsSuccessAction | FetchTopUserViewSettingsErrorAction

export const FETCH_TOP_USER_VIEW_SETTINGS = "FETCH_TOP_USER_VIEW_SETTINGS";
export const FETCH_TOP_USER_VIEW_SETTINGS_SUCCESS = "FETCH_TOP_USER_VIEW_SETTINGS_SUCCESS";
export const FETCH_TOP_USER_VIEW_SETTINGS_ERROR = "FETCH_TOP_USER_VIEW_SETTINGS_ERROR";

export function fetchTopUserViewSettings(
    viewIdentifier: string,
    userViewSettings: UserViewSettings
): FetchTopUserViewSettingsAction {
    return {
        type: FETCH_TOP_USER_VIEW_SETTINGS,
        viewIdentifier,
        userViewSettings
    };
}
export function fetchTopUserViewSettingsSuccess(
    viewIdentifier: string,
    topSettings: UserViewSettings[]
): FetchTopUserViewSettingsSuccessAction {
    return {
        type: FETCH_TOP_USER_VIEW_SETTINGS_SUCCESS,
        topSettings,
        viewIdentifier
    }
}
export function fetchTopUserViewSettingsError( error: any ): FetchTopUserViewSettingsErrorAction {
    return {
        type: FETCH_TOP_USER_VIEW_SETTINGS_ERROR,
        error
    };
}

export function fetchTopUserViewSettingsEpic(
    action$: Observable< FetchTopUserViewSettingsAction >,
    store$: StateObservable< AppState >
) {
    return action$.pipe(
        ofType(FETCH_TOP_USER_VIEW_SETTINGS),
        mergeMap(action => {
            const path = APP_CONFIG.baseWeb + `users/views/${ action.viewIdentifier }/settings/top`;

            return ajax(APP_CONFIG.getRequestObject(path)).pipe(
                map(response => fetchTopUserViewSettingsSuccess(
                    action.viewIdentifier,
                    response.response as UserViewSettings[]
                )),
                catchError(error => of(
                    fetchTopUserViewSettingsError(error),
                    eventNotificationError(error, store$, action.viewIdentifier, "fetchTopUserViewSettings")
                ))
            );
        })
    );
}


/**
 * SAVE USER VIEW SETTINGS
 */
export interface CreateUserViewSettingsAction extends ViewIdentifiable, UserViewSettingsProp, ReduxAction<typeof CREATE_USER_VIEW_SETTINGS> {}
export interface CreateUserViewSettingsSuccessAction extends UserViewSettingsProp, ReduxAction<typeof CREATE_USER_VIEW_SETTINGS_SUCCESS> {}
export interface CreateUserViewSettingsErrorAction extends ErrorAction<typeof CREATE_USER_VIEW_SETTINGS_ERROR> {}

export type CreateUserViewSettingsActionTypes = CreateUserViewSettingsAction | CreateUserViewSettingsSuccessAction | CreateUserViewSettingsErrorAction

export const CREATE_USER_VIEW_SETTINGS = "CREATE_USER_VIEW_SETTINGS";
export const CREATE_USER_VIEW_SETTINGS_SUCCESS = "CREATE_USER_VIEW_SETTINGS_SUCCESS";
export const CREATE_USER_VIEW_SETTINGS_ERROR = "CREATE_USER_VIEW_SETTINGS_ERROR";

export function createUserViewSettings( viewIdentifier: string, settings: UserViewSettings ): CreateUserViewSettingsAction {
    return { type: CREATE_USER_VIEW_SETTINGS, viewIdentifier, userViewSettings: settings };
}
export function createUserViewSettingsSuccess( settings: UserViewSettings ): CreateUserViewSettingsSuccessAction {
    return { type: CREATE_USER_VIEW_SETTINGS_SUCCESS, userViewSettings: settings };
}
export function createUserViewSettingsError( error: any ): CreateUserViewSettingsErrorAction {
    return { type: CREATE_USER_VIEW_SETTINGS_ERROR, error };
}

export function createUserViewSettingsEpic(
    action$: Observable< CreateUserViewSettingsAction >,
    store$: StateObservable<AppState>
) {
    return action$.pipe(
        ofType( CREATE_USER_VIEW_SETTINGS ),
        //exhaustMap: throw away all inits until the previous one finishes
        exhaustMap( action => {
             const path = APP_CONFIG.baseWeb + `users/views/${ action.viewIdentifier }/settings/`+ action.userViewSettings.title;
            const settingsParams = getUrlParamsFromViewSettings( action.userViewSettings );

            return ajax(
                APP_CONFIG.getRequestObject(
                    path + settingsParams,
                    RouteMethods.post,
                    action.userViewSettings
                )
            )
                .pipe(
                    mergeMap( response => of(
                        createUserViewSettingsSuccess( response.response as UserViewSettings ),
                        eventNotificationSuccess(),
                        fetchTopUserViewSettings( action.viewIdentifier, response.response as UserViewSettings )
                    )),
                    catchError( error => of(
                        createUserViewSettingsError( error ),
                        eventNotificationError( error, store$, action.viewIdentifier, "createUserViewSettings" )
                    ))
                );
        })
    );
}

/**
 * REPLACE USER VIEW SETTINGS
 */
export interface ReplaceUserViewSettingsAction extends ViewIdentifiable, UserViewSettingsProp, ReduxAction<typeof REPLACE_USER_VIEW_SETTINGS> {}
export interface ReplaceUserViewSettingsSuccessAction extends UserViewSettingsProp, ReduxAction<typeof REPLACE_USER_VIEW_SETTINGS_SUCCESS> {}
export interface ReplaceUserViewSettingsErrorAction extends ErrorAction<typeof REPLACE_USER_VIEW_SETTINGS_ERROR> {}

export type ReplaceUserViewSettingsActionTypes = ReplaceUserViewSettingsAction | ReplaceUserViewSettingsSuccessAction | ReplaceUserViewSettingsErrorAction

export const REPLACE_USER_VIEW_SETTINGS = "REPLACE_USER_VIEW_SETTINGS";
export const REPLACE_USER_VIEW_SETTINGS_SUCCESS = "REPLACE_USER_VIEW_SETTINGS_SUCCESS";
export const REPLACE_USER_VIEW_SETTINGS_ERROR = "REPLACE_USER_VIEW_SETTINGS_ERROR";

export function replaceUserViewSettings( viewIdentifier: string, settings: UserViewSettings ): ReplaceUserViewSettingsAction {
    return { type: REPLACE_USER_VIEW_SETTINGS, viewIdentifier, userViewSettings: settings };
}
export function replaceUserViewSettingsSuccess( settings: UserViewSettings ): ReplaceUserViewSettingsSuccessAction {
    return { type: REPLACE_USER_VIEW_SETTINGS_SUCCESS, userViewSettings: settings };
}
export function replaceUserViewSettingsError( error: any ): ReplaceUserViewSettingsErrorAction {
    return { type: REPLACE_USER_VIEW_SETTINGS_ERROR, error };
}

export function replaceUserViewSettingsEpic(
    action$: Observable< ReplaceUserViewSettingsAction >,
    store$: StateObservable<AppState>
) {
    return action$.pipe(
        ofType( REPLACE_USER_VIEW_SETTINGS ),
        //exhaustMap: throw away all inits until the previous one finishes
        exhaustMap( action => {
            const path = APP_CONFIG.baseWeb + `users/views/${ action.viewIdentifier }/settings/replace/`+ action.userViewSettings.userViewSettingsId.rawValue;
            const settingsParams = getUrlParamsFromViewSettings( action.userViewSettings );

            return ajax(
                APP_CONFIG.getRequestObject(
                    path + settingsParams,
                    RouteMethods.post,
                    action.userViewSettings
                )
            )
                .pipe(
                    mergeMap( response => of(
                        replaceUserViewSettingsSuccess( response.response as UserViewSettings ),
                        eventNotificationSuccess(),
                        fetchTopUserViewSettings( action.viewIdentifier, response.response as UserViewSettings )
                    )),
                    catchError( error => of(
                        replaceUserViewSettingsError( error ),
                        eventNotificationError( error, store$, action.viewIdentifier, "replaceUserViewSettings" )
                    ))
                );
        })
    );
}

/**
 * DELETE USER VIEW SETTINGS
 */
export interface DeleteUserViewSettingsAction extends ViewIdentifiable, UserViewSettingsProp, ReduxAction<typeof DELETE_USER_VIEW_SETTINGS> {}
export interface DeleteUserViewSettingsSuccessAction extends UserViewSettingsProp, ReduxAction<typeof DELETE_USER_VIEW_SETTINGS_SUCCESS> {}
export interface DeleteUserViewSettingsErrorAction extends ErrorAction<typeof DELETE_USER_VIEW_SETTINGS_ERROR> {}

export type DeleteUserViewSettingsActionTypes = DeleteUserViewSettingsAction | DeleteUserViewSettingsSuccessAction | DeleteUserViewSettingsErrorAction

export const DELETE_USER_VIEW_SETTINGS = "DELETE_USER_VIEW_SETTINGS";
export const DELETE_USER_VIEW_SETTINGS_SUCCESS = "DELETE_USER_VIEW_SETTINGS_SUCCESS";
export const DELETE_USER_VIEW_SETTINGS_ERROR = "DELETE_USER_VIEW_SETTINGS_ERROR";

export function deleteUserViewSettings( viewIdentifier: string, settings: UserViewSettings ): DeleteUserViewSettingsAction {
    return { type: DELETE_USER_VIEW_SETTINGS, viewIdentifier, userViewSettings: settings };
}
export function deleteUserViewSettingsSuccess( settings: UserViewSettings ): DeleteUserViewSettingsSuccessAction {
    return { type: DELETE_USER_VIEW_SETTINGS_SUCCESS, userViewSettings: settings };
}
export function deleteUserViewSettingsError( error: any ): DeleteUserViewSettingsErrorAction {
    return { type: DELETE_USER_VIEW_SETTINGS_ERROR, error };
}

export function deleteUserViewSettingsEpic(
    action$: Observable< DeleteUserViewSettingsAction >,
    store$: StateObservable<AppState>
) {
    return action$.pipe(
        ofType( DELETE_USER_VIEW_SETTINGS ),
        //exhaustMap: throw away all inits until the previous one finishes
        exhaustMap( action => {
            const path = APP_CONFIG.baseWeb + `users/views/${ action.viewIdentifier }/settings/`+ action.userViewSettings.userViewSettingsId.rawValue;

            return ajax(
                APP_CONFIG.getRequestObject(
                    path,
                    RouteMethods.delete
                )
            )
                .pipe(
                    mergeMap( response => of(
                        deleteUserViewSettingsSuccess( response.response as UserViewSettings ),
                        eventNotificationSuccess(),
                        fetchTopUserViewSettings( action.viewIdentifier, response.response as UserViewSettings )
                    )),
                    catchError( error => of(
                        deleteUserViewSettingsError( error ),
                        eventNotificationError( error, store$, action.viewIdentifier, "deleteUserViewSettings" )
                    ))
                );
        })
    );
}

/**
 * increment usage count
 */
export interface IncrementUsageCountAction extends ViewIdentifiable, ReduxAction<typeof INCREMENT_USAGE_COUNT> {
    id: UserViewSettingsId
}
export interface IncrementUsageCountSuccessAction extends ViewIdentifiable, ReduxAction<typeof INCREMENT_USAGE_COUNT_SUCCESS> {
    id: UserViewSettingsId
}
export interface IncrementUsageCountErrorAction extends ErrorAction<typeof INCREMENT_USAGE_COUNT_ERROR> {}

export type IncrementUsageCountActionTypes = IncrementUsageCountAction | IncrementUsageCountErrorAction | IncrementUsageCountSuccessAction

export const INCREMENT_USAGE_COUNT = "INCREMENT_USAGE_COUNT";
export const INCREMENT_USAGE_COUNT_SUCCESS = "INCREMENT_USAGE_COUNT_SUCCESS";
export const INCREMENT_USAGE_COUNT_ERROR = "INCREMENT_USAGE_COUNT_ERROR";

export function incrementViewSettingsUsageCount(viewIdentifier: string, id: UserViewSettingsId): IncrementUsageCountAction {
    return {
        type: INCREMENT_USAGE_COUNT,
        viewIdentifier,
        id
    };
}
export function incrementViewSettingsUsageCountSuccess(
    viewIdentifier: string,
    id: UserViewSettingsId
): IncrementUsageCountSuccessAction {
    return {
        type: INCREMENT_USAGE_COUNT_SUCCESS,
        viewIdentifier,
        id
    };
}
export function incrementViewSettingsUsageCountError(error: any): IncrementUsageCountErrorAction {
    return {
        type: INCREMENT_USAGE_COUNT_ERROR ,
        error
    };
}

export function incrementViewSettingsUsageCountEpic(
    action$: Observable< IncrementUsageCountAction >,
    store$: StateObservable< AppState >
) {
    return action$.pipe(
        ofType(INCREMENT_USAGE_COUNT),
        mergeMap(action => {

            const path = APP_CONFIG.baseWeb + `users/views/${action.viewIdentifier}/settings/${ action.id.userViewSettingsId.rawValue }/increment`;

            return ajax(APP_CONFIG.getRequestObject(path, RouteMethods.put)).pipe(
                map( _ => incrementViewSettingsUsageCountSuccess(action.viewIdentifier, action.id)),
                catchError(error => of(
                    incrementViewSettingsUsageCountError(error),
                    eventNotificationError(error, store$, action.viewIdentifier, "incrementViewSettingsUsageCount")
                ))
            );
        })
    );
}

/**
 * SORTING
 */
export interface ViewSortChangeAction extends ViewIdentifiable {
    type: typeof VIEW_SORT_CHANGE
    orderBy: SortModel
}

export const VIEW_SORT_CHANGE = "VIEW_SORT_CHANGE";

export function handleSortChange( viewIdentifier: string, orderBy: SortModel ): ViewSortChangeAction {
    return { type: VIEW_SORT_CHANGE, viewIdentifier, orderBy };
}



/**
 * search
 */
export interface ViewSearchChangeAction extends ViewIdentifiable {
    type: typeof HANDLE_SEARCH_CHANGE
    searchValue: string
}
export interface ViewSearchTriggeredAction extends ViewIdentifiable {
    type: typeof HANDLE_SEARCH_TRIGGERED
    searchValue: string
}

export type ViewSearchActionTypes = ViewSearchChangeAction | ViewSearchTriggeredAction

export const HANDLE_SEARCH_CHANGE = "HANDLE_SEARCH_CHANGE";
// fetching epics should listen for this action type executed
export const HANDLE_SEARCH_TRIGGERED = "HANDLE_SEARCH_TRIGGERED";

export function handleSearchChange( viewIdentifier: string, searchValue: string ): ViewSearchChangeAction {
    return { type: HANDLE_SEARCH_CHANGE, viewIdentifier, searchValue };
}

export function handleSearchTriggered( viewIdentifier: string, searchValue: string ): ViewSearchTriggeredAction {
    return { type: HANDLE_SEARCH_TRIGGERED, viewIdentifier, searchValue };
}

// debounce the user input by half a second and execute handle search triggered afterwards. this way not all keystrokes will initiate an server fetch
export function handleSearchEpic( action$: Observable< ViewSearchChangeAction > ) {
    return action$.pipe(
        ofType( HANDLE_SEARCH_CHANGE ),
        debounceTime(500),
        map( ( action: ViewSearchChangeAction  ) => handleSearchTriggered( action.viewIdentifier, action.searchValue ) )
    );
}

/**
 * column visibility state
 */
export interface ColumnVisibilityChangeAction extends ViewIdentifiable {
    type: typeof COLUMN_VISIBILITY_CHANGE_ACTION
    columnIdentifier: string
    isVisible: boolean
}

export const COLUMN_VISIBILITY_CHANGE_ACTION = "COLUMN_VISIBILITY_CHANGE_ACTION";

export function changeColumnVisibility(
    viewIdentifier: string,
    columnIdentifier: string,
    isVisible: boolean
): ColumnVisibilityChangeAction {
    return {type: COLUMN_VISIBILITY_CHANGE_ACTION, isVisible, columnIdentifier, viewIdentifier};
}

/**
 * SET COLUMNS OF LISTVIEW
 */
export interface SetListViewColumnsAction extends ReduxAction< typeof SET_LIST_VIEW_COLUMNS >, ViewIdentifiable {
    columns: ColumnList
}
export const SET_LIST_VIEW_COLUMNS = "SET_LIST_VIEW_COLUMNS";

export function setListViewColumns( viewIdentifier: string, columns: ColumnList ): SetListViewColumnsAction
{
    return { type: SET_LIST_VIEW_COLUMNS, viewIdentifier, columns };
}

/**
 * ROWS PER PAGE CHANGE
 */
export interface ViewRowsPerPageChangeAction extends ViewIdentifiable {
    type: typeof HANDLE_ROWS_PER_PAGE_CHANGE
    rowsPerPage: number
}
export const HANDLE_ROWS_PER_PAGE_CHANGE = "HANDLE_ROWS_PER_PAGE_CHANGE";

export function handleRowsPerPageChange( params: PageChangeParams ): ViewRowsPerPageChangeAction {
    return {
        type: HANDLE_ROWS_PER_PAGE_CHANGE,
        viewIdentifier: params.viewIdentifier,
        rowsPerPage: params.pageSize
    };
}

/**
 * CURRENT PAGE NUMBER CHANGE
 */
export interface ViewPageNumberChangeAction extends ViewIdentifiable {
    type: typeof HANDLE_PAGE_NUMBER_CHANGE
    params: PageChangeParams
}

export const HANDLE_PAGE_NUMBER_CHANGE = "HANDLE_PAGE_NUMBER_CHANGE";

export function handlePageChange( params: PageChangeParams ): ViewPageNumberChangeAction {
    return { type: HANDLE_PAGE_NUMBER_CHANGE, params, viewIdentifier: params.viewIdentifier };
}

/**
 * FILTER DATA
 */
export interface ViewDataFilterAddAction extends ViewIdentifiable, ReduxAction<typeof VIEW_DATA_FILTER_ADD>, SingleActiveDataFilterProp {}

export interface ViewDataFilterRemoveAction extends ViewIdentifiable, ReduxAction<typeof VIEW_DATA_FILTER_REMOVE>, SingleActiveDataFilterProp {}

export type ViewDataFilterActionTypes = ViewDataFilterAddAction | ViewDataFilterRemoveAction

export const VIEW_DATA_FILTER_ADD = "VIEW_DATA_FILTER_ADD";
export const VIEW_DATA_FILTER_REMOVE = "VIEW_DATA_FILTER_REMOVE";

export function addDataFilter<T>( viewIdentifier: string, filter: ActiveDataFilter<T> ): ViewDataFilterAddAction {
    return { type: VIEW_DATA_FILTER_ADD, filter, viewIdentifier };
}
export function removeDataFilter<T>( viewIdentifier: string, filter: ActiveDataFilter<T>): ViewDataFilterRemoveAction {
    return { type: VIEW_DATA_FILTER_REMOVE, filter, viewIdentifier };
}

/**
 * CHANGE VIEW TITLE
 */
export interface ChangeViewTitleAction extends ReduxAction<typeof CHANGE_VIEW_TITLE>, ViewIdentifiable {
    title: string
}

export const CHANGE_VIEW_TITLE = "CHANGE_VIEW_TITLE";

export function changeViewTitle( viewIdentifier: ViewIdentifier, title: string ): ChangeViewTitleAction {
    return { type: CHANGE_VIEW_TITLE, viewIdentifier, title };
}

/**
 * SEND USER TO PAGE
 *
 * redirect functionality mostly used after saving data to the backend.
 * therefore should be mainly used in Success functions.
 * The actual redirect happens in the App.tsx Component::ComponentDidUpdate
 */
export interface SendUserToPageAction extends ViewIdentifiable, ReduxAction<typeof SEND_USER_TO_PAGE> {
    path: string
}
export interface SendUserToPageSuccessAction extends ViewIdentifiable, ReduxAction<typeof SEND_USER_TO_PAGE_SUCCESS>{}
export type SendUserToPageActionTypes = SendUserToPageSuccessAction | SendUserToPageAction

export const SEND_USER_TO_PAGE = "SEND_USER_TO_PAGE";
export const SEND_USER_TO_PAGE_SUCCESS = "SEND_USER_TO_PAGE_SUCCESS";

/**
 * Navigates the user to a specific page within the application.
 *
 * This function is used to trigger a navigation action, sending the user to a specified path
 * while associating the navigation with a specific view or context identifier. Commonly used
 * for programmatic routing and state updates to support user-driven workflows.
 *
 * @param path - The target URL or route path to navigate to. Must be a valid application route.
 * @param viewIdentifier - A unique identifier for the destination view or context. Used to
 *                          associate logic or state changes with the navigation event.
 * @return An action object of type `SendUserToPageAction`, which includes the type of action,
 *         the target path, and the view identifier. This action is typically dispatched to
 *         a Redux-like state management system.
 * @throws Error if either `path` or `viewIdentifier` are undefined or invalid.
 */
export function sendUserToPage( path: string, viewIdentifier: string ): SendUserToPageAction {
    return { type: SEND_USER_TO_PAGE, path, viewIdentifier };
}
export function sendUserToPageSuccess( viewIdentifier: string ): SendUserToPageSuccessAction {
    return { type: SEND_USER_TO_PAGE_SUCCESS, viewIdentifier };
}

/**
 * activate unsaved data protection
 */
export interface  ActivateUnsavedDataProtectionAction extends ReduxAction<typeof ACTIVATE_UNSAVED_DATA_PROTECTION> {}
export interface DisableUnsavedDataProtectionAction extends ReduxAction<typeof DISABLE_UNSAVED_DATA_PROTECTION> {}

export type UnsavedDataProtectionActionTypes = ActivateUnsavedDataProtectionAction | DisableUnsavedDataProtectionAction

export const ACTIVATE_UNSAVED_DATA_PROTECTION = "ACTIVATE_UNSAVED_DATA_PROTECTION";
export const DISABLE_UNSAVED_DATA_PROTECTION = "DISABLE_UNSAVED_DATA_PROTECTION";

export function activateUnsavedDataProtection(): ActivateUnsavedDataProtectionAction {
    return { type: ACTIVATE_UNSAVED_DATA_PROTECTION };
}

export function disableUnsavedDataProtection(): DisableUnsavedDataProtectionAction {
    return { type: DISABLE_UNSAVED_DATA_PROTECTION };
}
